import React, { useContext, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField/TextField';
import PageLayout from '../../components/common/page-layout';
import { ModalContext } from '../../contexts/modal';
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import FirmsTable from '../../components/sales/firms-table';
import { useCreateFirm } from '../../api/hooks/sales';
import { useQueryClient } from 'react-query';
import { FIRMS_KEY } from '../../api/hooks/cache-keys';
import BankAccountTable from '../../components/inventory/bank-accounts-table';
import NewBankAccountForm from '../../components/sales/new-bank-account-form';

export default function Firms() {
  const { setOpen, setContent, setTitle } = useContext(ModalContext);
  const [subsidaries, setSubsidaries] = useState(false);
  function openFirmModal() {
    const cont = <CreateFirmModal />;
    setContent(cont);
    setTitle('ساخت شرکت جدید');
    setOpen(true);
  }

  function openAccountModal() {
    const cont = <NewBankAccountForm />;
    setContent(cont);
    setTitle('ساخت حساب جدید');
    setOpen(true);
  }

  return (
    <PageLayout pageTitle="شرکای تجاری">
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Stack direction="row" justifyContent="space-between">
            <Button variant="contained" color="primary" sx={{ mb: 3 }} onClick={openFirmModal}>
              افزودن شریک
            </Button>
            <FormControlLabel
              control={
                <Switch checked={subsidaries} onChange={(e) => setSubsidaries(e.target.checked)} />
              }
              label={
                <Typography fontSize="small" color="textSecondary">
                  فقط زیرمجموعه‌ها
                </Typography>
              }
              labelPlacement="end"
            />
          </Stack>
          <FirmsTable subsidaries={subsidaries} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack direction="row" justifyContent="space-between">
            <Button variant="contained" color="primary" sx={{ mb: 3 }} onClick={openAccountModal}>
              افزودن حساب
            </Button>
          </Stack>

          <BankAccountTable />
        </Grid>
      </Grid>
    </PageLayout>
  );
}

function CreateFirmModal() {
  const nameRef = useRef();
  const subRef = useRef();
  const phoneRef = useRef();
  const addressRef = useRef();
  const postalRef = useRef();
  const ecoRef = useRef();
  const nationalRef = useRef();
  const registryRef = useRef();
  const [isPerson, setIsPerson] = useState(false);
  const [error, setError] = useState(false);
  const createFirm = useCreateFirm();
  const { setOpen } = useContext(ModalContext);
  const queryClient = useQueryClient();

  async function onSubmit(e) {
    e.preventDefault();
    try {
      setError(false);
      await createFirm.mutateAsync({
        name: nameRef.current.value,
        isSubsidary: subRef.current?.value ?? false,
        ecoId: ecoRef.current?.value,
        nationalId: nationalRef.current.value,
        address: addressRef.current.value,
        phoneNumber: phoneRef.current.value,
        postalCode: postalRef.current.value,
        registryId: registryRef.current?.value,
      });
      await queryClient.invalidateQueries(FIRMS_KEY());
      setOpen(false);
      setError();
    } catch {
      setError(true);
    }
  }

  return (
    <form autoComplete={false} onSubmit={onSubmit}>
      <Box sx={{ my: 2 }}>
        <TextField label="نام شریک" variant="filled" size="small" fullWidth inputRef={nameRef} />
      </Box>
      <Box sx={{ my: 2 }}>
        <FormControl variant="filled" size="small" fullWidth>
          <ToggleButtonGroup
            color="primary"
            value={isPerson}
            exclusive
            fullWidth
            onChange={(e, newValue) => setIsPerson(newValue)}
            aria-label="Platform"
          >
            <ToggleButton value={true}>شخص</ToggleButton>
            <ToggleButton value={false}>شرکت</ToggleButton>
          </ToggleButtonGroup>
        </FormControl>
      </Box>
      {isPerson ? (
        <>
          <Box sx={{ my: 1 }}>
            <TextField
              label="کد ملی"
              inputProps={{ pattern: '[0-9]{10}' }}
              variant="filled"
              size="small"
              type="numeric"
              helperText="۱۰ رقم"
              fullWidth
              inputRef={nationalRef}
            />
          </Box>
          <Box sx={{ my: 1 }}>
            <TextField
              label="کد پستی"
              inputProps={{ pattern: '[0-9]{10}' }}
              variant="filled"
              size="small"
              type="numeric"
              helperText="۱۰ رقم"
              fullWidth
              inputRef={postalRef}
            />
          </Box>
          <Box sx={{ my: 2 }}>
            <TextField
              label="شماره تلفن"
              variant="filled"
              size="small"
              type="numeric"
              fullWidth
              inputRef={phoneRef}
            />
          </Box>

          <Box sx={{ my: 2 }}>
            <TextField
              label="آدرس"
              variant="filled"
              size="small"
              type="numeric"
              fullWidth
              multiline
              maxRows={3}
              inputRef={addressRef}
            />
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ my: 2 }}>
            <FormControl variant="filled" size="small" fullWidth>
              <InputLabel>نوع ارتباط</InputLabel>
              <Select fullWidth label="حقیقی/حقوقی" placeholder="حقیقی/حقوقی" inputRef={subRef}>
                {[
                  { name: 'زیرمجموعه', value: true },
                  { name: 'مشتری', value: false },
                ].map((b, i) => (
                  <MenuItem value={b.value} key={i}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ my: 1 }}>
            <TextField
              label="شناسه ملی"
              inputProps={{ pattern: '[0-9]{11}' }}
              variant="filled"
              size="small"
              type="numeric"
              helperText="۱۱ رقم"
              fullWidth
              inputRef={nationalRef}
            />
          </Box>

          <Box sx={{ my: 1 }}>
            <TextField
              label="کد اقتصادی"
              inputProps={{ pattern: '[0-9]{12}' }}
              variant="filled"
              size="small"
              type="numeric"
              helperText="۱۲ رقم"
              fullWidth
              inputRef={ecoRef}
            />
          </Box>

          <Box sx={{ my: 1 }}>
            <TextField
              label="شماره ثبت"
              inputProps={{ pattern: '[0-9]{6}' }}
              variant="filled"
              size="small"
              helperText="۶ رقم"
              type="numeric"
              fullWidth
              inputRef={registryRef}
            />
          </Box>

          <Box sx={{ my: 1 }}>
            <TextField
              label="کد پستی"
              inputProps={{ pattern: '[0-9]{10}' }}
              variant="filled"
              size="small"
              type="numeric"
              fullWidth
              inputRef={postalRef}
            />
          </Box>
          <Box sx={{ my: 2 }}>
            <TextField
              label="شماره تلفن"
              variant="filled"
              size="small"
              type="numeric"
              fullWidth
              inputRef={phoneRef}
            />
          </Box>

          <Box sx={{ my: 2 }}>
            <TextField
              label="آدرس"
              variant="filled"
              size="small"
              type="numeric"
              fullWidth
              multiline
              maxRows={3}
              inputRef={addressRef}
            />
          </Box>
        </>
      )}

      <Button
        type="submit"
        variant="contained"
        color={error ? 'error' : 'primary'}
        sx={{ mb: 4 }}
        fullWidth
      >
        ایجاد
      </Button>
    </form>
  );
}
